/**
 * AufgabenBoard — the kanban (Board) view of the Aufgaben queue, additive to the
 * ListView + DataTable. It groups the CURRENTLY-FILTERED task set (the same
 * `visible` rows the DataTable renders — active preset + search already applied)
 * into status lanes and renders each row as a compact, navigable task card.
 *
 * The board introduces NO data fetch and NO decision logic: it is a grouped
 * read+navigate surface. A card click reuses the host screen's row-activation
 * (`onOpenTask` = the exact List `openRow`: deep-link routable processes, else
 * open the decision peek), so List and Board navigate identically. The Kunde
 * EntityLink reuses the List's `onOpenKunde` closure.
 *
 * Lanes: Offen · In Arbeit · Wartet auf Freigabe · Erledigt, always shown; the
 * terminal `cancelled` bucket renders as a muted "Abgebrochen" lane at the end
 * ONLY when it is non-empty (a clean board keeps no permanently-empty lane).
 * Rows arrive already sorted (due-asc) from the host, so lanes inherit that order.
 *
 * [Source: apps/web/src/features/aufgaben/AufgabenScreen.tsx (openRow / makeColumns cells)]
 * [Source: apps/web/src/features/communication-v2/EingangScreen.tsx (SegmentedControl view-mode sibling)]
 */
import { useMemo } from 'react';
import type { TaskStatus } from '@brokly/shared';
import { cn } from '../../../lib/cn';
import { StatusDot } from '../../../components/ui/StatusDot';
import { Badge } from '../../../components/ui/Badge';
import { CountBadge } from '../../../components/ui/CountBadge';
import { EntityLink } from '../../../components/ui/EntityLink';
import {
  processLabel,
  statusLabel,
  STATUS_TONE,
  derivePriority,
  PRIORITY_LABEL,
  PRIORITY_VARIANT,
  faellig,
} from '../api/aufgaben-api';
import type { TaskRow } from '../types';

interface AufgabenBoardProps {
  /** The already-filtered + due-sorted rows (same set the DataTable renders). */
  rows: TaskRow[];
  /** Row/card activation — the host's `openRow` (deep-link or open the peek). */
  onOpenTask: (task: TaskRow) => void;
  /** Kunde navigation — the host's closure (person/firma → /kunden/$id). */
  onOpenKunde: (subjectType: string, id: string) => void;
}

/** Lane definition: the status column key + whether it is the muted terminal lane. */
interface Lane {
  status: TaskStatus;
  muted?: boolean;
}

/** Column order (design.md flow): open → in_progress → awaiting_approval → done,
 *  then the muted cancelled lane (rendered only when it has cards). */
const LANES: Lane[] = [
  { status: 'open' },
  { status: 'in_progress' },
  { status: 'awaiting_approval' },
  { status: 'done' },
  { status: 'cancelled', muted: true },
];

/** One task card — mirrors the DataTable cells (title + category, Kunde/Police,
 *  Fälligkeit, Priorität, status). A div[role=button] (not a <button>) so the
 *  inner Kunde EntityLink stays a valid nested interactive element. */
function TaskCard({
  task,
  onOpenTask,
  onOpenKunde,
}: {
  task: TaskRow;
  onOpenTask: (task: TaskRow) => void;
  onOpenKunde: (subjectType: string, id: string) => void;
}) {
  const prio = derivePriority(task);
  const f = faellig(task.due_at);
  const kunde = task.kunde_name;
  const canLink =
    !!kunde &&
    !!task.subject_id &&
    (task.subject_type === 'person' || task.subject_type === 'firma');

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpenTask(task)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenTask(task);
        }
      }}
      className="flex cursor-pointer flex-col gap-2 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-ring hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {/* Title + category sub-line · Priorität badge */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 leading-tight">
          <div className="truncate text-sm font-medium text-foreground">
            {task.title ?? '(ohne Titel)'}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">{processLabel(task.process)}</div>
        </div>
        <Badge variant={PRIORITY_VARIANT[prio]} className="shrink-0">
          {PRIORITY_LABEL[prio]}
        </Badge>
      </div>

      {/* Kunde / Police */}
      {(kunde || task.subject) && (
        <div className="min-w-0 leading-tight">
          {canLink ? (
            <EntityLink onActivate={() => onOpenKunde(task.subject_type!, task.subject_id!)}>
              {kunde}
            </EntityLink>
          ) : (
            <span className="text-sm text-foreground">{kunde ?? '-'}</span>
          )}
          {task.subject && (
            <div className="truncate text-xs text-muted-foreground">{task.subject}</div>
          )}
        </div>
      )}

      {/* Fälligkeit · status */}
      <div className="flex items-center justify-between gap-2 text-xs">
        <StatusDot tone={f.tone} label={f.text} />
        <StatusDot tone={STATUS_TONE[task.status]} label={statusLabel(task.status)} />
      </div>
    </div>
  );
}

export function AufgabenBoard({ rows, onOpenTask, onOpenKunde }: AufgabenBoardProps) {
  // Bucket the rows by status once (order preserved → lanes stay due-sorted).
  const byStatus = useMemo(() => {
    const map = new Map<TaskStatus, TaskRow[]>();
    for (const lane of LANES) map.set(lane.status, []);
    for (const r of rows) map.get(r.status)?.push(r);
    return map;
  }, [rows]);

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {LANES.map((lane) => {
        const items = byStatus.get(lane.status) ?? [];
        // The muted terminal lane (Abgebrochen) only appears when it has cards.
        if (lane.muted && items.length === 0) return null;
        return (
          <section
            key={lane.status}
            aria-label={statusLabel(lane.status)}
            className={cn(
              'flex w-72 shrink-0 flex-col rounded-xl bg-secondary/30 p-2',
              lane.muted && 'opacity-75',
            )}
          >
            <div className="mb-2 flex items-center justify-between gap-2 px-1">
              <StatusDot tone={STATUS_TONE[lane.status]} label={statusLabel(lane.status)} />
              <CountBadge count={items.length} />
            </div>
            <div className="flex max-h-[calc(100vh-18rem)] flex-col gap-2 overflow-y-auto pr-0.5">
              {items.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border px-2 py-6 text-center text-xs text-muted-foreground/60">
                  Keine Aufgaben
                </p>
              ) : (
                items.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onOpenTask={onOpenTask}
                    onOpenKunde={onOpenKunde}
                  />
                ))
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
